import { Directive, ElementRef, forwardRef, HostListener, Renderer2, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { LoggerService } from '../../services/logger-services/logger.service';

@Directive({
  selector: '[contenteditable][ngModel]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ContenteditableModelDirective),
      multi: true
    }
  ]
})
export class ContenteditableModelDirective implements ControlValueAccessor, OnDestroy {
  public onChange = (value: string) => {};
  public onTouched = () => {};
  public lastKnownValue: string = '';
  public isUpdatingContent = false;
  public static minHeight: number = 44;
  public maxHeightWithFilePills: number = 110;
  public maxHeightWithoutFilePills: number = 160;
  public validDomains: string[] = [];
  
  // IME support properties
  public isComposing = false;
  public compositionData = '';
  public preCompositionValue = '';
  
  // File pills observer
  private filePillsObserver: MutationObserver | null = null;
  private lastFilePillsState: boolean = false;

  /** Error template for CONTENTEDITABLE DIRECTIVE */
  private readonly contenteditableError = (error: any) => 
    `[CONTENTEDITABLE DIRECTIVE] - ${JSON.stringify(error)}`;

  constructor(
    public elementRef: ElementRef, 
    private readonly renderer: Renderer2,
    private readonly loggerService: LoggerService
  ) {
    this.initializeElement();
    // Set initial empty state
    this.renderer.addClass(this.elementRef.nativeElement, 'is-empty');
    // Initialize file pills observer after a short delay to ensure DOM is ready
    setTimeout(() => this.initializeFilePillsObserver(), 100);
  }

  public static setUseNewChatbot(value: number): void {
    this.minHeight = value;
  }

  public setValidDomains(domains: string[]): void {
    this.validDomains = domains.map(domain => domain.toUpperCase());
  }
  
  @HostListener('input', ['$event'])
  onInput(event?: Event): void {
    try {
      if (this.isUpdatingContent) return;
      
      // Don't process input during IME composition at all - let IME handle it naturally
      if (this.isComposing) {
        return;
      }
        
      const element = this.elementRef.nativeElement;
      let plainText = element.innerText || '';
      
      // Only normalize '@' symbols when NOT in IME composition
      const normalizedText = this.normalizeAtSymbol(plainText);
      
      // If normalization changed the text, update the element
      if (normalizedText !== plainText) {
        this.isUpdatingContent = true;
        const selection = this.saveSelection();
        element.innerText = normalizedText;
        this.restoreSelection(selection);
        this.isUpdatingContent = false;
        plainText = normalizedText;
      }
        
      if (plainText !== this.lastKnownValue) {
        this.lastKnownValue = plainText;
        this.onChange(plainText);
        this.updatePlaceholderVisibility(element, plainText);
        this.adjustHeight();
        
        // Trigger domain highlighting immediately for manual entry
        if (this.validDomains.length > 0 && plainText.includes('@')) {
          // Use shorter delay for better responsiveness
          setTimeout(() => {
            this.highlightDomainMentions();
          }, 100);
        }
      }
      
      // Always ensure placeholder state is correct
      this.updatePlaceholderVisibility(element, plainText);
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "onInput");
    }
  }

  @HostListener('blur')
  onBlur(): void {
    try {
      const element = this.elementRef.nativeElement;
      const plainText = element.innerText || '';
      this.updatePlaceholderVisibility(element, plainText);
      this.onTouched();
      
      // Trigger highlighting on blur to catch any missed highlights
      if (this.validDomains.length > 0 && plainText.includes('@')) {
        setTimeout(() => {
          this.highlightDomainMentions();
        }, 50);
      }
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "onBlur");
    }
  }

  @HostListener('focus')
  onFocus(): void {
    try {
      const element = this.elementRef.nativeElement;
      const plainText = element.innerText || '';
      this.updatePlaceholderVisibility(element, plainText);
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "onFocus");
    }
  }

  @HostListener('compositionstart', ['$event'])
  onCompositionStart(event: CompositionEvent): void {
    try {
      this.isComposing = true;
      this.preCompositionValue = this.elementRef.nativeElement.innerText || '';
      
      // Hide placeholder during composition for better UX
      const element = this.elementRef.nativeElement;
      this.renderer.removeClass(element, 'is-empty');
      this.renderer.addClass(element, 'has-content');
      
      // Add a data attribute to signal IME composition to parent components
      this.renderer.setAttribute(element, 'data-ime-composing', 'true');
      
      // Add visual indicator for @ symbol during IME composition
      this.addIMECompositionStyles();
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "onCompositionStart");
    }
  }

  @HostListener('compositionupdate', ['$event'])
  onCompositionUpdate(event: CompositionEvent): void {
    try {
      this.compositionData = event.data || '';
      
      // Check if user is typing @ during composition (for CJK languages)
      if (this.compositionData.includes('@')) {
        this.handleCJKAtSymbol();
      }
      
      // Don't trigger onChange during composition update
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "onCompositionUpdate");
    }
  }

  @HostListener('compositionend', ['$event'])
  onCompositionEnd(event: CompositionEvent): void {
    try {
      this.isComposing = false;
      this.compositionData = '';
      
      // Remove the IME composition signal and styles
      const element = this.elementRef.nativeElement;
      this.renderer.removeAttribute(element, 'data-ime-composing');
      this.removeIMECompositionStyles();
      
      // Force update after composition ends with proper delay to ensure IME is fully done
      setTimeout(() => {
        try {
          let plainText = element.innerText || '';
          
          // Clean up any duplicate characters that might have been created during composition
          // This handles the double @＠ issue
          plainText = plainText.replace(/[@＠]+/g, '@');  // Replace multiple @ symbols with single @
          
          // Normalize '@' symbols after IME composition is complete
          const normalizedText = this.normalizeAtSymbol(plainText);
          
          // If normalization or cleanup changed the text, update seamlessly
          if (normalizedText !== plainText || element.innerText !== normalizedText) {
            this.isUpdatingContent = true;
            const selection = this.saveSelection();
            element.innerText = normalizedText;
            // Preserves cursor position after normalization
            this.restoreSelection(selection);
            this.isUpdatingContent = false;
            plainText = normalizedText;
          }
          
          if (plainText !== this.lastKnownValue) {
            this.lastKnownValue = plainText;
            this.onChange(plainText);
            this.updatePlaceholderVisibility(element, plainText);
            this.adjustHeight();
            
            // Enhanced @ detection for CJK languages (Chinese, Japanese, Korean)
            const previousText = this.preCompositionValue || '';
            const newText = plainText;
            
            // Check if @ was added during IME composition (handles all CJK input methods)
            if (this.hasNewAtSymbol(previousText, newText)) {
              // Dispatch a custom input event to trigger domain switch updateSuggestions
              setTimeout(() => {
                const inputEvent = new Event('input', { bubbles: true });
                element.dispatchEvent(inputEvent);
              }, 10);
            }
            
            // Trigger domain highlighting for IME input
            if (this.validDomains.length > 0 && plainText.includes('@')) {
              setTimeout(() => {
                this.highlightDomainMentions();
              }, 100);
            }
          }
        } catch (timeoutError) {
          this.loggerService.logError(this.contenteditableError(timeoutError), "onCompositionEnd-timeout");
        }
      }, 0);
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "onCompositionEnd");
    }
  }

  @HostListener('keyup', ['$event'])
  onKeyUp(event: KeyboardEvent): void {
    try {
      // During IME composition, only prevent keys that would interfere with parent component logic
      // Allow IME to handle its own keys naturally
      if (this.isComposing) {
        const parentInterferenceKeys = [
          'Enter', 'Escape', 'Tab'  // Only prevent keys that would trigger parent component actions
        ];
        
        if (parentInterferenceKeys.includes(event.key)) {
          event.stopPropagation();
          // Don't preventDefault - let IME handle the key naturally
          return;
        }
      }
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "onKeyUp");
    }
  }

  @HostListener('keypress', ['$event'])
  onKeyPress(event: KeyboardEvent): void {
    try {
      // During IME composition, only prevent keys that would interfere with parent component logic
      if (this.isComposing) {
        const parentInterferenceKeys = [
          'Enter', 'Escape', 'Tab'  // Only prevent keys that would trigger parent component actions
        ];
        
        if (parentInterferenceKeys.includes(event.key) || event.keyCode === 13) {
          event.stopPropagation();
          // Don't preventDefault for IME keys - let IME handle them
          return;
        }
      }
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "onKeyPress");
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    try {
      const element = this.elementRef.nativeElement;

      // During IME composition, only prevent keys that would interfere with parent component logic
      // Let IME handle its own keys (backspace, arrows, etc.) naturally
      if (this.isComposing) {
        const parentInterferenceKeys = [
          'Enter',           // Prevent triggering send/submit actions
          'Escape',          // Prevent closing dialogs/dropdowns
          'Tab'              // Prevent focus changes
        ];
        
        if (parentInterferenceKeys.includes(event.key)) {
          // Only stop propagation, don't preventDefault so IME can still use these keys
          event.stopPropagation();
          return;
        }
        
        // For all other keys during composition (backspace, arrows, space, delete), 
        // let them pass through normally so IME can handle them
        return;
      }

      // Handle @ character - trigger domain suggestions instead of typing
      // Allow @ to pass through during IME composition, handle it in compositionend instead
      if (event.key === '@' && !event.ctrlKey && !event.metaKey && !this.isComposing) {
        event.preventDefault();
        // Insert @ symbol and trigger domain suggestions
        this.insertText('@');
        
        // Trigger domain suggestions after @ is inserted
        setTimeout(() => {
          // Dispatch a custom input event to trigger domain switch updateSuggestions
          const inputEvent = new Event('input', { bubbles: true });
          element.dispatchEvent(inputEvent);
        }, 10);
        return;
      }

      // Handle Enter key
      if (event.key === 'Enter' && !this.isComposing) {
        if (event.shiftKey) {
          setTimeout(() => {
            this.adjustHeight();
            const plainText = element.innerText || '';
            this.updatePlaceholderVisibility(element, plainText);
          }, 0);
        } 
        return;
      }

      // Handle Tab key - prevent losing focus
      if (event.key === 'Tab' && !this.isComposing) {
        event.preventDefault();
        this.insertText('\t');
        return;
      }

      // Handle Delete and Backspace to update placeholder
      if ((event.key === 'Delete' || event.key === 'Backspace') && !this.isComposing) {
        setTimeout(() => {
          const plainText = element.innerText || '';
          this.updatePlaceholderVisibility(element, plainText);
          
          // Re-highlight after deletion if @ symbols remain
          if (this.validDomains.length > 0 && plainText.includes('@')) {
            setTimeout(() => {
              this.highlightDomainMentions();
            }, 100);
          }
        }, 0);
      }

      // Handle Space bar - trigger highlighting when user finishes typing a domain
      if ((event.key === ' ' || event.key === 'Spacebar') && !this.isComposing) {
        if (this.validDomains.length > 0) {
          setTimeout(() => {
            const plainText = element.innerText || '';
            if (plainText.includes('@')) {
              this.highlightDomainMentions();
            }
          }, 100);
        }
      }
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "onKeyDown");
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    try {
      // Don't interfere with paste during IME composition
      if (this.isComposing) {
        return;
      }
      
      event.preventDefault();
      
      const clipboardData = event.clipboardData;
      let textData = clipboardData?.getData('text/plain') || '';
      
      if (textData) {
        // First normalize '@' symbols from different keyboards
        textData = this.normalizeAtSymbol(textData);
        
        // Enhanced paste handling for @mentions
        textData = this.processPastedText(textData);
        
        // Only insert text if there's something left after processing
        if (textData) {
          this.insertText(textData);
          
          // Trigger highlighting if pasted text contains valid @mentions
          if (this.validDomains.length > 0 && textData.includes('@')) {
            setTimeout(() => {
              this.highlightDomainMentions();
            }, 100);
          }
        }
      }
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "onPaste");
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    try {
      if (this.isUpdatingContent) return;
      
      const element = this.elementRef.nativeElement;
      const currentValue = element.innerText || '';
      
      // Normalize '@' symbols even when setting value programmatically
      const normalizedValue = this.normalizeAtSymbol(value || '');
      
      if (normalizedValue !== currentValue) {
        this.isUpdatingContent = true;
        this.lastKnownValue = normalizedValue;
        
        // Set content as plain text with normalized '@' symbols
        element.innerText = normalizedValue;
        this.updatePlaceholderVisibility(element, normalizedValue);
        this.adjustHeight();
        
        // Place cursor at end
        this.placeCaretAtEnd(element);
        
        this.isUpdatingContent = false;
        
        // Trigger highlighting if value contains @ domains
        if (this.validDomains.length > 0 && normalizedValue && normalizedValue.includes('@')) {
          setTimeout(() => {
            this.highlightDomainMentions();
          }, 100);
        }
      }
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "writeValue");
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    try {
      const element = this.elementRef.nativeElement;
      this.renderer.setProperty(element, 'contentEditable', !isDisabled);
      
      if (isDisabled) {
        this.renderer.addClass(element, 'disabled');
        this.renderer.setStyle(element, 'opacity', '0.6');
      } else {
        this.renderer.removeClass(element, 'disabled');
        this.renderer.removeStyle(element, 'opacity');
      }
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "setDisabledState");
    }
  }

  ngOnDestroy(): void {
    try {
      // Disconnect file pills observer
      if (this.filePillsObserver) {
        this.filePillsObserver.disconnect();
        this.filePillsObserver = null;
      }
      
      // Remove placeholder styles if this is the last instance
      const placeholderStyle = document.getElementById('contenteditable-placeholder-styles');
      if (placeholderStyle) {
        placeholderStyle.remove();
      }
      
      // Remove IME composition styles
      const imeStyle = document.getElementById('ime-composition-styles');
      if (imeStyle) {
        imeStyle.remove();
      }
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "ngOnDestroy");
    }
  }

  /**
   * Initialize MutationObserver to watch for file pills container changes
   * This ensures height recalculates when files are added/removed
   */
  private initializeFilePillsObserver(): void {
    try {
      if (typeof window === 'undefined' || typeof MutationObserver === 'undefined') return;
      
      const element = this.elementRef.nativeElement;
      const promptSection = element.closest('.prompt-section') || element.closest('.prompt-section-new');
      
      if (!promptSection) return;
      
      // Watch for changes in the prompt section (file pills being added/removed)
      this.filePillsObserver = new MutationObserver((mutations) => {
        // Check if file pills state changed
        const currentFilePillsState = this.hasFilePills();
        
        if (currentFilePillsState !== this.lastFilePillsState) {
          this.lastFilePillsState = currentFilePillsState;
          // File pills state changed - recalculate height
          this.onFilePillsChanged();
        }
      });
      
      // Observe the prompt section for child list changes (file pills container)
      this.filePillsObserver.observe(promptSection, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
      });
      
      // Also watch form-input for class changes (has-file-pills class)
      const formInput = element.closest('.form-input');
      if (formInput) {
        const formInputObserver = new MutationObserver((mutations) => {
          const currentFilePillsState = this.hasFilePills();
          
          if (currentFilePillsState !== this.lastFilePillsState) {
            this.lastFilePillsState = currentFilePillsState;
            this.onFilePillsChanged();
          }
        });
        
        formInputObserver.observe(formInput, {
          attributes: true,
          attributeFilter: ['class']
        });
      }
      
      // Initialize the last state
      this.lastFilePillsState = this.hasFilePills();
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "initializeFilePillsObserver");
    }
  }

  /**
   * Public method to trigger height recalculation when file pills change
   * Can be called externally from parent components
   */
  public onFilePillsChanged(): void {
    try {
      // Recalculate height with new max height based on file pills presence
      this.adjustHeight();
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "onFilePillsChanged");
    }
  }

  //helper methods
  public initializeElement(): void {
    try {
      const element = this.elementRef.nativeElement;
      
      // Basic contenteditable setup
      this.renderer.setProperty(element, 'contentEditable', 'true');
      this.renderer.setProperty(element, 'spellcheck', 'true');
      
      // Textarea-like styling
      this.renderer.setStyle(element, 'white-space', 'pre-wrap');
      this.renderer.setStyle(element, 'word-wrap', 'break-word');
      this.renderer.setStyle(element, 'overflow-wrap', 'break-word');
      this.renderer.setStyle(element, 'overflow-y', 'auto');
      this.renderer.setStyle(element, 'resize', 'none');
      this.renderer.setStyle(element, 'box-sizing', 'border-box');
      this.renderer.setStyle(element, 'outline', 'none');
      // Enhanced font family with Korean support
      this.renderer.setStyle(element, 'font-family', '"Nunito Sans", "Inter", "Malgun Gothic", "맑은 고딕", "Apple SD Gothic Neo", "Noto Sans CJK KR", "Noto Sans Korean", "Apple Color Emoji", "Segoe UI Emoji", sans-serif');
      this.renderer.setStyle(element, 'font-size', 'inherit');
      this.renderer.setStyle(element, 'line-height', 'inherit');
      
      // Placeholder setup
      this.renderer.setAttribute(element, 'data-placeholder', 'Ask Freya');
      
      // Add placeholder styling
      this.addPlaceholderStyles();
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "initializeElement");
    }
  }

  public addPlaceholderStyles(): void {
    try {
      // Check if placeholder styles are already added
      if (document.getElementById('contenteditable-placeholder-styles')) {
        return;
      }

      // Create and inject placeholder CSS
      const style = this.renderer.createElement('style');
      style.id = 'contenteditable-placeholder-styles';
      style.textContent = `
        [contenteditable][data-placeholder].is-empty:before {
          content: attr(data-placeholder);
          color: #999;
          pointer-events: none;
          position: absolute;
          opacity: 0.7;
        }
        [contenteditable].is-empty:focus:before {
          opacity: 0.5;
        }
        [contenteditable].has-content:before,
        [contenteditable]:not(.is-empty):before {
          display: none !important;
          visibility: hidden !important;
        }
        [contenteditable][data-placeholder].is-empty:before {
          display: block !important;
          visibility: visible !important;
        }
      `;
      
      this.renderer.appendChild(document.head, style);
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "addPlaceholderStyles");
    }
  }

  public getFilePillsHeight(): number {
    try {
      const element = this.elementRef.nativeElement;
      
      // Method 1: Check if form-input has 'has-file-pills' class (set by Angular template)
      const formInput = element.closest('.form-input') as HTMLElement;
      if (formInput && formInput.classList.contains('has-file-pills')) {
        // Look for file-pills-container as a sibling (it's outside form-input, inside prompt-section)
        const promptSection = formInput.closest('.prompt-section') || formInput.closest('.prompt-section-new');
        if (promptSection) {
          const filePillsContainer = promptSection.querySelector('.file-pills-container') as HTMLElement;
          if (filePillsContainer && filePillsContainer.children.length > 0) {
            // Return actual height of the file pills container including margins
            const computedStyle = window.getComputedStyle(filePillsContainer);
            const marginTop = parseFloat(computedStyle.marginTop) || 0;
            const marginBottom = parseFloat(computedStyle.marginBottom) || 0;
            const actualHeight = filePillsContainer.offsetHeight + marginTop + marginBottom;
            return actualHeight;
          }
        }
      }
      
      // Method 2: Direct search in prompt-section (fallback)
      const promptSection = element.closest('.prompt-section') || element.closest('.prompt-section-new');
      if (promptSection) {
        const filePillsContainer = promptSection.querySelector('.file-pills-container') as HTMLElement;
        if (filePillsContainer && filePillsContainer.children.length > 0) {
          const computedStyle = window.getComputedStyle(filePillsContainer);
          const marginTop = parseFloat(computedStyle.marginTop) || 0;
          const marginBottom = parseFloat(computedStyle.marginBottom) || 0;
          const actualHeight = filePillsContainer.offsetHeight + marginTop + marginBottom;
          return actualHeight;
        }
      }
      
      return 0;
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "getFilePillsHeight");
      return 0;
    }
  }

  /** True when there is no user-visible text (including lone newline from an empty contenteditable). */
  private isEffectivelyEmpty(value: string): boolean {
    if (!value || value.length === 0) {
      return true;
    }
    // Browsers often leave a single newline in innerText when the field looks empty
    return value.includes('\n') && value.length === 1;
  }

  /**
   * Contenteditable stays :not(:empty) after delete (e.g. leftover <br>). Clear DOM so placeholder CSS works.
   */
  private ensureEffectivelyEmptyDom(element: HTMLElement): void {
    const text = element.innerText || '';
    if (!this.isEffectivelyEmpty(text)) {
      return;
    }
    if (element.childNodes.length === 0) {
      return;
    }
    this.isUpdatingContent = true;
    element.innerHTML = '';
    this.isUpdatingContent = false;
  }

  public updatePlaceholderVisibility(element: HTMLElement, value: string): void {
    try {
      const isEmpty = this.isEffectivelyEmpty(value);

      if (isEmpty) {
        this.ensureEffectivelyEmptyDom(element);
        this.renderer.addClass(element, 'is-empty');
        this.renderer.removeClass(element, 'has-content');
      } else {
        this.renderer.removeClass(element, 'is-empty');
        this.renderer.addClass(element, 'has-content');
      }
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "updatePlaceholderVisibility");
    }
  }

  public highlightDomainMentions(): void {
    try {
      if (this.isUpdatingContent || typeof window === 'undefined' || this.validDomains.length === 0) return;
      
      const element = this.elementRef.nativeElement;
      const currentText = element.innerText || '';
      
      // Don't highlight if text is empty
      if (!currentText.trim()) return;
      
      // Check if text contains CJK characters (Chinese, Japanese, Korean)
      const cjkPattern = /[\u3400-\u4DBF\u4E00-\u9FFF\u3040-\u309F\u30A0-\u30FF\uAC00-\uD7AF]/;
      const hasCJKCharacters = cjkPattern.test(currentText);
      
      // Use enhanced CJK highlighting if CJK characters are present
      if (hasCJKCharacters) {
        this.highlightCJKDomainMentions();
        return;
      }
      
      // Save the current selection
      const selection = this.saveSelection();
      
      this.isUpdatingContent = true;
      
      try {
        // Create regex to match @domain patterns (word boundaries)
        const domainPattern = /@(\w+)/g;
        let highlightedHTML = currentText;
        let match;
        const matches: Array<{text: string, domain: string, startIndex: number, endIndex: number, isValid: boolean}> = [];
        
        // Find all @domain mentions
        while ((match = domainPattern.exec(currentText)) !== null) {
          const domain = match[1].toUpperCase();
          const isValid = this.validDomains.includes(domain);
          matches.push({
            text: match[0],
            domain: domain,
            startIndex: match.index,
            endIndex: match.index + match[0].length,
            isValid: isValid
          });
        }
        
        // Replace matches with highlighted spans (in reverse order to maintain positions)
        matches.reverse().forEach(matchData => {
          const { text, domain, startIndex, endIndex, isValid } = matchData;
          if (isValid) {
            // Enhanced styling
            const replacement = `<span style="color: #4166eb; font-weight: bold; background-color: rgba(65, 102, 235, 0.1); border-radius: 3px; padding: 1px 2px;" contenteditable="false" data-domain="${domain}">${text}</span>`;
            
            highlightedHTML = highlightedHTML.substring(0, startIndex) + 
                             replacement + 
                             highlightedHTML.substring(endIndex);
          }
        });
        
        // Only update if there are changes and we have highlights
        if (element.innerHTML !== highlightedHTML && matches.some(m => m.isValid)) {
          element.innerHTML = highlightedHTML;
          
          // For better cursor positioning after highlighting, place cursor at end if it was near the end
          setTimeout(() => {
            if (selection && selection.start >= currentText.length - 2) {
              // If cursor was at or near the end, place it at the end
              this.placeCaretAtEnd(element);
            } else {
              // Otherwise try to restore original position
              this.restoreSelection(selection);
            }
          }, 0);
        }
        
      } catch (highlightError) {
        this.loggerService.logError(this.contenteditableError(highlightError), "highlightDomainMentions-inner");
        // Fallback to plain text
        element.innerText = currentText;
      }
      
      this.isUpdatingContent = false;
      
      // Update placeholder visibility after highlighting
      this.updatePlaceholderVisibility(element, currentText);
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "highlightDomainMentions");
    }
  }

  public insertText(text: string): void {
    try {
      if (typeof window === 'undefined') return;
      
      // Don't insert text during IME composition to avoid conflicts
      if (this.isComposing) return;
      
      // Normalize '@' symbols before inserting
      const normalizedText = this.normalizeAtSymbol(text);
      
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        // If no selection, append to end
        const element = this.elementRef.nativeElement;
        const currentText = element.innerText || '';
        element.innerText = currentText + normalizedText;
        this.placeCaretAtEnd(element);
      } else {
        // Insert at current selection
        const range = selection.getRangeAt(0);
        range.deleteContents();
        
        const textNode = document.createTextNode(normalizedText);
        range.insertNode(textNode);
        range.setStartAfter(textNode);
        range.collapse(true);
        
        selection.removeAllRanges();
        selection.addRange(range);
      }
      
      // Trigger input event and highlighting
      setTimeout(() => {
        try {
          const element = this.elementRef.nativeElement;
          const plainText = element.innerText || '';
          if (plainText !== this.lastKnownValue) {
            this.lastKnownValue = plainText;
            this.onChange(plainText);
            this.updatePlaceholderVisibility(element, plainText);
            this.adjustHeight();
            
            // Auto-highlight any @mentions in the inserted text
            if (this.validDomains.length > 0 && normalizedText.includes('@')) {
              setTimeout(() => {
                this.highlightDomainMentions();
              }, 50);
            }
          }
        } catch (timeoutError) {
          this.loggerService.logError(this.contenteditableError(timeoutError), "insertText-timeout");
        }
      }, 0);
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "insertText");
    }
  }

  public placeCaretAtEnd(element: HTMLElement): void {
    try {
      if (typeof window === 'undefined') return;
      
      const selection = window.getSelection();
      if (!selection) return;

      const range = document.createRange();
      range.selectNodeContents(element);
      range.collapse(false);
      
      selection.removeAllRanges();
      selection.addRange(range);
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "placeCaretAtEnd");
    }
  }

  /**
   * Get the current max height based on file pills presence and viewport height
   * Returns adjusted max height for smaller viewports
   */
  public getCurrentMaxHeight(): number {
    const filePillsHeight = this.getFilePillsHeight();
    
    // If file pills are present, use the reduced max height
    if (filePillsHeight > 0) {
      return this.maxHeightWithFilePills;
    }
    
    // Check viewport height and return appropriate max height
    if (typeof window !== 'undefined') {
      const viewportHeight = window.innerHeight;
      
      if (viewportHeight <= 550) {
        return 110; // Smallest viewports
      } 
      else if (viewportHeight > 550 && viewportHeight <= 632) {
        return 120; // Medium-small viewports
      }
      else if (viewportHeight > 632 && viewportHeight <= 700) {
        return 130; // Medium-small viewports
      }
    }
    
    // Default max height for larger viewports
    return this.maxHeightWithoutFilePills;
  }

  /**
   * Check if file pills are currently present
   */
  public hasFilePills(): boolean {
    return this.getFilePillsHeight() > 0;
  }

  public adjustHeight(): void {
    try {
      const element: HTMLElement = this.elementRef.nativeElement;
      
      // Store scroll information
      const scrollTop = element.scrollTop;
      const wasAtBottom = scrollTop >= (element.scrollHeight - element.clientHeight - 5);
      
      // Get file pills height first (file pills are outside textarea)
      const filePillsHeight = this.getFilePillsHeight();
      
      // Get current max height based on file pills presence
      const currentMaxHeight = this.getCurrentMaxHeight();
      
      // Temporarily set height to auto to get the scroll height
      this.renderer.setStyle(element, 'height', 'auto');
      
      // Calculate the required height for textarea (based on text content only)
      const scrollHeight = element.scrollHeight;
      const newHeight = Math.max(scrollHeight, ContenteditableModelDirective.minHeight);
      const finalHeight = Math.min(newHeight, currentMaxHeight);
      
      // Set the calculated height (textarea height only, not including file pills)
      this.renderer.setStyle(element, 'height', `${finalHeight}px`);
      
      // Calculate height increase from textarea text content
      const textareaHeightIncrease = finalHeight - ContenteditableModelDirective.minHeight;
      
      // Total height increase = textarea growth + file pills height
      // This ensures the entire input area grows only upward
      const totalHeightIncrease = textareaHeightIncrease + filePillsHeight;
      
      // Apply negative margin to grow upward (only in new-session mode)
      const promptSection = element.closest('.prompt-section') as HTMLElement;
      if (promptSection && promptSection.classList.contains('new-session')) {
        this.renderer.setStyle(promptSection, 'margin-top', `-${totalHeightIncrease}px`);
      } else if (promptSection) {
        this.renderer.setStyle(promptSection, 'margin-top', '0px');
      }
      
      // Handle overflow and scrolling based on current max height
      if (scrollHeight > currentMaxHeight) {
        // Content exceeds max height - enable scrolling
        this.renderer.setStyle(element, 'overflow-y', 'auto');
        this.renderer.removeClass(element, 'prompt-ready');
        this.renderer.addClass(element, 'scrollable-prompt-ready');
        
        // Maintain scroll position or scroll to bottom if user was at bottom
        if (wasAtBottom) {
          setTimeout(() => {
            element.scrollTop = element.scrollHeight;
          }, 0);
        } else {
          element.scrollTop = Math.min(scrollTop, element.scrollHeight - element.clientHeight);
        }
      } else {
        // Content fits - hide scrollbar
        this.renderer.setStyle(element, 'overflow-y', 'hidden');
        this.renderer.removeClass(element, 'scrollable-prompt-ready');
        this.renderer.addClass(element, 'prompt-ready');
        element.scrollTop = 0;
      }
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "adjustHeight");
    }
  }

  public resetHeight(): void {
    try {
      const element: HTMLElement = this.elementRef.nativeElement;
      this.renderer.setStyle(element, 'overflow-y', 'hidden');
      this.renderer.setStyle(element, 'height', `${ContenteditableModelDirective.minHeight}px`);
      this.renderer.addClass(element, 'prompt-ready');
      this.renderer.removeClass(element, 'scrollable-prompt-ready');
      
      // Reset margin-top when height is reset, but account for file pills if present
      const promptSection = element.closest('.prompt-section') as HTMLElement;
      if (promptSection) {
        const filePillsHeight = this.getFilePillsHeight();
        if (promptSection.classList.contains('new-session') && filePillsHeight > 0) {
          // If file pills are present in new-session mode, apply negative margin to grow upward
          this.renderer.setStyle(promptSection, 'margin-top', `-${filePillsHeight}px`);
        } else {
          this.renderer.setStyle(promptSection, 'margin-top', '0px');
        }
      }
      element.scrollTop = 0;
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "resetHeight");
    }
  }

  public removeHighlighting(): void {
    try {
      if (this.isUpdatingContent || typeof window === 'undefined') return;
      
      const element = this.elementRef.nativeElement;
      const currentText = element.innerText || '';
      
      // Convert back to plain text if currently highlighted
      if (element.innerHTML !== element.innerText) {
        const selection = this.saveSelection();
        this.isUpdatingContent = true;
        element.innerText = currentText;
        this.restoreSelection(selection);
        this.isUpdatingContent = false;
      }
      
      // Always check placeholder after removing highlighting
      this.updatePlaceholderVisibility(element, currentText);
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "removeHighlighting");
    }
  }

  public saveSelection(): any {
    try {
      if (typeof window === 'undefined') return null;
      
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return null;
      
      const element = this.elementRef.nativeElement;
      const range = sel.getRangeAt(0);
      
      // Calculate text-based positions
      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents(element);
      preCaretRange.setEnd(range.startContainer, range.startOffset);
      const start = preCaretRange.toString().length;
      
      const preEndRange = range.cloneRange();
      preEndRange.selectNodeContents(element);
      preEndRange.setEnd(range.endContainer, range.endOffset);
      const end = preEndRange.toString().length;
      
      return { start, end };
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "saveSelection");
      return null;
    }
  }

  public restoreSelection(savedSelection: any): void {
    try {
      if (!savedSelection || typeof window === 'undefined') return;
      
      const element = this.elementRef.nativeElement;
      const sel = window.getSelection();
      if (!sel) return;
      
      // Find text positions in the DOM
      const walker = document.createTreeWalker(
        element,
        NodeFilter.SHOW_TEXT,
        null
      );
      
      const range = document.createRange();
      let charIndex = 0;
      let startSet = false;
      let endSet = false;
      let node;
      
      while (node = walker.nextNode()) {
        const nodeLength = node.textContent?.length || 0;
        
        if (!startSet && charIndex + nodeLength >= savedSelection.start) {
          range.setStart(node, savedSelection.start - charIndex);
          startSet = true;
        }
        
        if (startSet && !endSet && charIndex + nodeLength >= savedSelection.end) {
          range.setEnd(node, savedSelection.end - charIndex);
          endSet = true;
          break;
        }
        
        charIndex += nodeLength;
      }
      
      if (startSet) {
        if (!endSet) {
          range.collapse(true);
        }
        sel.removeAllRanges();
        sel.addRange(range);
      }
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "restoreSelection");
    }
  }

  public escapeHtml(text: string): string {
    try {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "escapeHtml");
      return text;
    }
  }

  // Public utility methods
  public focus(): void {
    try {
      this.elementRef.nativeElement.focus();
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "focus");
    }
  }

  public blur(): void {
    try {
      this.elementRef.nativeElement.blur();
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "blur");
    }
  }

  public clear(): void {
    try {
      const element = this.elementRef.nativeElement;
      element.innerText = '';
      this.lastKnownValue = '';
      this.onChange('');
      this.updatePlaceholderVisibility(element, '');
      this.adjustHeight();
      
      // Force placeholder to show
      this.renderer.addClass(element, 'is-empty');
      this.renderer.removeClass(element, 'has-content');
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "clear");
    }
  }

  public insertTextAtCursor(text: string): void {
    try {
      this.insertText(text);
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "insertTextAtCursor");
    }
  }

  // Public method to manually trigger highlighting
  public triggerHighlighting(): void {
    try {
      if (this.validDomains.length > 0) {
        this.highlightDomainMentions();
      }
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "triggerHighlighting");
    }
  }

  // Public method to force highlighting with better integration
  public getPlaceholder(): string {
    try {
      const element = this.elementRef.nativeElement;
      return element.getAttribute('data-placeholder') || '';
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "getPlaceholder");
      return '';
    }
  }

  // CJK Language Support Methods

  /**
   * Normalize various '@' symbol variations to standard English '@' (U+0040)
   * Supports Cross-keyboard support
   */
  public normalizeAtSymbol(text: string): string {
    try {
      if (!text) return text;
      
      // Comprehensive '@' symbol normalization
      // Handles all major keyboard layouts and input methods
      let normalizedText = text;
      
      // Primary fullwidth @ (most common in CJK keyboards) - U+FF20
      normalizedText = normalizedText.replace(/＠/g, '@');
      
      // Additional Unicode variations that might be typed as '@'
      const atVariations = [
        '\uff20',  // ＠ - Fullwidth Commercial At (Japanese/Chinese/Korean IME)
        '\u0040',  // @ - Standard Commercial At (already standard, for completeness)
        '\u0254',  // ɔ - Sometimes confused in certain layouts
        '\u0252',  // ɒ - Sometimes confused in certain layouts
      ];
      
      // Replace systematically without affecting performance
      atVariations.forEach(variation => {
        if (variation !== '\u0040') { // Don't replace standard '@' with itself
          // Use global replacement for efficiency
          const regex = new RegExp(escapeRegExp(variation), 'g');
          normalizedText = normalizedText.replace(regex, '@');
        }
      });
      
      // Handles composed characters that result in @ symbols
      // Handle any remaining fullwidth characters that might represent @
      normalizedText = normalizedText.replace(/［＠］/g, '@'); // Bracketed fullwidth @
      normalizedText = normalizedText.replace(/（＠）/g, '@'); // Parenthesized fullwidth @
      
      return normalizedText;
      
      // Helper function to escape special regex characters
      function escapeRegExp(string: string): string {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      }
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "normalizeAtSymbol");
      return text;
    }
  }

  /**
   * Add visual styles for IME composition with @ symbol
   */
  public addIMECompositionStyles(): void {
    try {
      if (document.getElementById('ime-composition-styles')) {
        return;
      }

      const style = this.renderer.createElement('style');
      style.id = 'ime-composition-styles';
      style.textContent = `
        [contenteditable][data-ime-composing] {
          position: relative;
        }
        [contenteditable][data-ime-composing]:after {
          content: "";
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(90deg, #4166eb, #7c3aed);
          animation: ime-pulse 1.5s ease-in-out infinite;
        }
        @keyframes ime-pulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
        /* Visual indicator for @ symbol during CJK input */
        [contenteditable][data-ime-composing] .ime-at-indicator {
          background-color: rgba(65, 102, 235, 0.2);
          border-radius: 3px;
          padding: 1px 2px;
          border-bottom: 2px solid #4166eb;
        }
      `;
      
      this.renderer.appendChild(document.head, style);
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "addIMECompositionStyles");
    }
  }

  /**
   * Remove IME composition styles
   */
  public removeIMECompositionStyles(): void {
    try {
      const style = document.getElementById('ime-composition-styles');
      if (style) {
        style.remove();
      }
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "removeIMECompositionStyles");
    }
  }

  /**
   * Handle @ symbol detection during CJK IME composition
   */
  public handleCJKAtSymbol(): void {
    try {
      if (typeof window === 'undefined') return;
      
      const element = this.elementRef.nativeElement;
      const selection = window.getSelection();
      
      if (selection && selection.rangeCount > 0) {
        // Add visual indicator for @ symbol during composition
        const range = selection.getRangeAt(0);
        const atIndex = this.compositionData.indexOf('@');
        
        if (atIndex !== -1) {
          // Add a temporary visual indicator
          this.renderer.addClass(element, 'cjk-at-composing');
          
          // Remove the class after a short delay
          setTimeout(() => {
            this.renderer.removeClass(element, 'cjk-at-composing');
          }, 300);
        }
      }
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "handleCJKAtSymbol");
    }
  }

  /**
   * Check if a new @ symbol was added during IME composition
   * This works for Chinese (Pinyin, Zhuyin), Japanese (Hiragana, Katakana), and Korean (Hangul)
   */
  public hasNewAtSymbol(previousText: string, newText: string): boolean {
    try {
      const previousAtCount = (previousText.match(/@/g) || []).length;
      const newAtCount = (newText.match(/@/g) || []).length;
      
      // Check if @ count increased
      if (newAtCount > previousAtCount) {
        return true;
      }
      
      // Check if @ position changed (for replacement scenarios in CJK input)
      if (newAtCount === previousAtCount && newAtCount > 0) {
        const previousLastAt = previousText.lastIndexOf('@');
        const newLastAt = newText.lastIndexOf('@');
        return newLastAt > previousLastAt;
      }
      
      return false;
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "hasNewAtSymbol");
      return false;
    }
  }

  /**
   * Process pasted text to preserve valid @mentions while removing invalid ones
   */
  public processPastedText(text: string): string {
    try {
      if (!text) {
        return '';
      }
      
      if (this.validDomains.length === 0) {
        // If no valid domains configured, remove all @ symbols
        return text.replace(/@/g, '');
      }
      
      // Find all @domain patterns in the pasted text
      const domainPattern = /@(\w+)/g;
      let processedText = text;
      let match;
      const replacements: Array<{original: string, replacement: string, index: number}> = [];
      
      // Collect all @domain matches
      while ((match = domainPattern.exec(text)) !== null) {
        const fullMatch = match[0];  // @MPR
        const domain = match[1];     // MPR
        const index = match.index;
        
        // Check if this is a valid domain
        const isValidDomain = this.validDomains.includes(domain.toUpperCase());
        
        if (isValidDomain) {
          // Keep valid @mentions as they are
          replacements.push({
            original: fullMatch,
            replacement: fullMatch, // Keep unchanged
            index: index
          });
        } else {
          // Remove @ from invalid domains, keeping just the text
          replacements.push({
            original: fullMatch,
            replacement: domain, // Remove @ symbol
            index: index
          });
        }
      }
      
      // Apply replacements in reverse order to maintain indices
      replacements.reverse().forEach(replacement => {
        const before = processedText.substring(0, replacement.index);
        const after = processedText.substring(replacement.index + replacement.original.length);
        processedText = before + replacement.replacement + after;
      });
      
      return processedText;
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "processPastedText");
      return text;
    }
  }

  /**
   * Enhanced domain highlighting for CJK languages
   * Handles Chinese, Japanese, and Korean characters properly
   */
  public highlightCJKDomainMentions(): void {
    try {
      if (this.isUpdatingContent || typeof window === 'undefined' || this.validDomains.length === 0) return;
      
      const element = this.elementRef.nativeElement;
      const currentText = element.innerText || '';
      
      // Don't highlight if text is empty
      if (!currentText.trim()) return;
      
      // Save the current selection
      const selection = this.saveSelection();
      
      this.isUpdatingContent = true;
      
      try {
        // Enhanced regex for CJK characters - matches @domain followed by CJK chars, space, or word boundary
        const cjkDomainPattern = /@(\w+)(?=[\s\u3400-\u4DBF\u4E00-\u9FFF\u3040-\u309F\u30A0-\u30FF\uAC00-\uD7AF]|$)/g;
        let highlightedHTML = currentText;
        let match;
        const matches: Array<{text: string, domain: string, startIndex: number, endIndex: number, isValid: boolean}> = [];
        
        // Find all @domain mentions (including those followed by CJK characters)
        while ((match = cjkDomainPattern.exec(currentText)) !== null) {
          const domain = match[1].toUpperCase();
          const isValid = this.validDomains.includes(domain);
          matches.push({
            text: match[0],
            domain: domain,
            startIndex: match.index,
            endIndex: match.index + match[0].length,
            isValid: isValid
          });
        }
        
        // Replace matches with highlighted spans (in reverse order to maintain positions)
        matches.reverse().forEach(matchData => {
          const { text, domain, startIndex, endIndex, isValid } = matchData;
          if (isValid) {
            // Enhanced styling for CJK support
            const replacement = `<span style="color: #4166eb; font-weight: bold; background-color: rgba(65, 102, 235, 0.1); border-radius: 3px; padding: 1px 2px;" contenteditable="false" data-domain="${domain}" data-cjk-mention="true">${text}</span>`;
            
            highlightedHTML = highlightedHTML.substring(0, startIndex) + 
                             replacement + 
                             highlightedHTML.substring(endIndex);
          }
        });
        
        // Only update if there are changes and we have highlights
        if (element.innerHTML !== highlightedHTML && matches.some(m => m.isValid)) {
          element.innerHTML = highlightedHTML;
          
          // Better cursor positioning for CJK text
          setTimeout(() => {
            if (selection && selection.start >= currentText.length - 2) {
              this.placeCaretAtEnd(element);
            } else {
              this.restoreSelection(selection);
            }
          }, 0);
        }
        
      } catch (highlightError) {
        this.loggerService.logError(this.contenteditableError(highlightError), "highlightCJKDomainMentions-inner");
        element.innerText = currentText;
      }
      
      this.isUpdatingContent = false;
      this.updatePlaceholderVisibility(element, currentText);
    } catch (error) {
      this.loggerService.logError(this.contenteditableError(error), "highlightCJKDomainMentions");
    }
  }
}